import { useState, type FormEvent } from "react";

import { createReview, type Review } from "../../lib/api/lidoo.functions";
import { useI18n } from "../../lib/i18n";

export function Stars({ value, size = "sm" }: { value: number; size?: "sm" | "lg" }) {
  const cls = size === "lg" ? "text-xl" : "text-sm";
  return (
    <span className={`${cls} tracking-tight`} aria-label={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= Math.round(value) ? "text-[#2C4FD6]" : "text-[#1C2430]/15"}>
          ★
        </span>
      ))}
    </span>
  );
}

export function RatingSummary({
  avg,
  count,
}: {
  avg: number | null;
  count: number;
}) {
  const { t } = useI18n();
  if (!avg || count === 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Stars value={avg} />
      <span className="li-mono text-[12px] text-[#1C2430]">
        {avg.toFixed(1)} ({count} {t("reviews_count")})
      </span>
    </span>
  );
}

// Google's rating, always labelled as Google's. Their terms require the
// attribution, and mixing it into our own star average would be dishonest
// anyway: 4.2 from 4,654 Google reviewers means something different from 4.2
// from six of ours.
export function GoogleRating({
  rating,
  count,
  size = "sm",
}: {
  rating: number | null;
  count: number | null;
  size?: "sm" | "lg";
}) {
  if (rating == null || !count) return null;
  const big = size === "lg";
  return (
    <span className="inline-flex items-center gap-1.5">
      <svg viewBox="0 0 24 24" className={big ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.2-2.2H12v4.1h6.6c-.1 1.1-.9 2.8-2.5 3.9l3.8 3c2.3-2.1 3.6-5.2 3.6-8.8z" />
        <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.8-3c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5l-4 3.1C3.2 21.3 7.3 24 12 24z" />
        <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.2 6.6C.4 8.2 0 10.1 0 12s.4 3.8 1.2 5.4l4-3.1z" />
        <path fill="#EA4335" d="M12 4.7c2.3 0 3.8 1 4.7 1.8l3.4-3.3C18 1.2 15.2 0 12 0 7.3 0 3.2 2.7 1.2 6.6l4 3.1c1-2.9 3.6-5 6.8-5z" />
      </svg>
      <span className={`li-mono ${big ? "text-[15px]" : "text-[12px]"} text-[#1C2430]`}>
        <span className="font-semibold">{rating.toFixed(1)}</span>
        <span className="text-[#5B6472]"> ({count.toLocaleString("en-US")})</span>
      </span>
    </span>
  );
}

const BOOKED_VIA_KEYS: Record<string, string> = {
  whatsapp: "booked_whatsapp",
  lidoo: "booked_lidoo",
  phone: "booked_phone",
  walkin: "booked_walkin",
  other: "booked_other",
};

function monthLabel(visitMonth: string): string {
  const [y, m] = visitMonth.split("-").map(Number);
  if (!y || !m) return visitMonth;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

function ReviewForm({ clubSlug, onPosted }: { clubSlug: string; onPosted: (r: Review) => void }) {
  const { t, lang } = useI18n();
  // Starts at 0, not 5. A form that arrives pre-filled with five stars is a
  // form that collects five stars: people submit the default rather than
  // their opinion, and a review set built that way is worth nothing.
  const [rating, setRating] = useState(0);
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errorKey, setErrorKey] = useState("review_generic_err");
  const [form, setForm] = useState({
    name: "",
    contact: "",
    title: "",
    body: "",
    visitMonth: "",
    bookedVia: "whatsapp" as "whatsapp" | "lidoo" | "phone" | "walkin" | "other",
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState("sending");
    try {
      const res = await createReview({
        data: {
          clubSlug,
          authorName: form.name,
          authorContact: form.contact,
          rating,
          title: form.title,
          body: form.body,
          visitMonth: form.visitMonth,
          bookedVia: form.bookedVia,
          locale: lang,
        },
      });
      if (res.ok) {
        setState("done");
        onPosted({
          id: Date.now(),
          author_name: form.name,
          rating,
          title: form.title,
          body: form.body,
          visit_month: form.visitMonth,
          booked_via: form.bookedVia,
          created_at: new Date().toISOString(),
        });
      } else {
        setErrorKey(
          res.reason === "spam"
            ? "review_spam_err"
            : res.reason === "duplicate"
              ? "review_duplicate_err"
              : "review_generic_err",
        );
        setState("error");
      }
    } catch {
      setErrorKey("review_generic_err");
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="rounded-xl border border-[#2C4FD6]/40 bg-[#2C4FD6]/5 p-6">
        <p className="li-display text-lg font-bold text-[#1C2430]">{t("review_ok_t")}</p>
        <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">{t("review_ok_d")}</p>
      </div>
    );
  }

  const field =
    "li-display h-11 w-full rounded-lg border border-[#1C2430]/15 bg-white/70 px-3 text-sm text-[#1C2430] placeholder:text-[#5B6472] focus:border-[#2C4FD6] focus:outline-none";
  const label = "li-mono mb-1 block text-[11px] uppercase tracking-wider text-[#5B6472]";

  return (
    <form onSubmit={submit} className="rounded-xl border border-[#1C2430]/10 bg-white/50 p-5">
      <h3 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
        {t("review_form_title")}
      </h3>
      <p className="mt-1 text-sm text-[#5B6472]">{t("review_form_sub")}</p>

      <div className="mt-4">
        <span className={label}>{t("review_rating")}</span>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              aria-label={`${n} / 5`}
              aria-pressed={n === rating}
              title={`${n} / 5`}
              className={`text-2xl transition-transform active:scale-90 ${
                n <= rating ? "text-[#2C4FD6]" : "text-[#1C2430]/20 hover:text-[#2C4FD6]/50"
              }`}
            >
              ★
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="rv-name" className={label}>
            {t("review_name")}
          </label>
          <input
            id="rv-name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={field}
          />
        </div>
        <div>
          <label htmlFor="rv-contact" className={label}>
            {t("review_contact")}
          </label>
          <input
            id="rv-contact"
            type="email"
            required
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
            className={field}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="rv-title" className={label}>
          {t("review_headline")}
        </label>
        <input
          id="rv-title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className={field}
        />
      </div>

      <div className="mt-4">
        <label htmlFor="rv-body" className={label}>
          {t("review_body")}
        </label>
        <textarea
          id="rv-body"
          required
          minLength={20}
          rows={4}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          className="li-display w-full rounded-lg border border-[#1C2430]/15 bg-white/70 px-3 py-2 text-sm text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="rv-month" className={label}>
            {t("review_visit_month")}
          </label>
          <input
            id="rv-month"
            type="month"
            required
            max={todayMonth()}
            value={form.visitMonth}
            onChange={(e) => setForm({ ...form, visitMonth: e.target.value })}
            className={field}
          />
        </div>
        <div>
          <span className={label}>{t("review_booked_via")}</span>
          <div className="flex flex-wrap gap-1.5">
            {(["whatsapp", "lidoo", "phone", "walkin", "other"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setForm({ ...form, bookedVia: v })}
                className={`li-mono rounded-md border px-2 py-1.5 text-[10px] transition-colors ${
                  form.bookedVia === v
                    ? "border-[#2C4FD6] bg-[#2C4FD6] text-[#F2EFE9]"
                    : "border-[#1C2430]/20 text-[#1C2430] hover:border-[#2C4FD6]"
                }`}
              >
                {t(BOOKED_VIA_KEYS[v])}
              </button>
            ))}
          </div>
        </div>
      </div>

      {state === "error" ? <p className="mt-3 text-sm text-[#B03030]">{t(errorKey)}</p> : null}

      <button
        type="submit"
        disabled={state === "sending" || rating === 0}
        className="li-display mt-5 h-12 rounded-lg bg-[#2C4FD6] px-5 text-[15px] font-bold text-[#F2EFE9] transition-transform hover:bg-[#243FB0] active:translate-y-[1px] disabled:opacity-60"
      >
        {state === "sending" ? t("review_sending") : t("review_send")}
      </button>
    </form>
  );
}

function todayMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, "0")}`;
}

export function ReviewsSection({
  clubSlug,
  initialReviews,
  avg,
  count,
}: {
  clubSlug: string;
  initialReviews: Review[];
  avg: number | null;
  count: number;
}) {
  const { t } = useI18n();
  const [reviews, setReviews] = useState(initialReviews);

  return (
    <section className="mt-12">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="li-display text-2xl font-bold tracking-tight text-[#1C2430]">
          {t("reviews_title")}
        </h2>
        <RatingSummary avg={avg} count={count} />
      </div>

      {reviews.length === 0 ? (
        <p className="mt-3 text-sm text-[#5B6472]">{t("no_reviews_yet")}</p>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-xl border border-[#1C2430]/10 bg-white/50 p-4">
              <div className="flex items-center justify-between gap-3">
                <Stars value={r.rating} />
                <span className="li-mono text-[10px] text-[#5B6472]">{monthLabel(r.visit_month)}</span>
              </div>
              {r.title ? (
                <p className="li-display mt-2 font-bold text-[#1C2430]">{r.title}</p>
              ) : null}
              <p className="mt-1.5 text-sm leading-relaxed text-[#1C2430]/90">{r.body}</p>
              <p className="li-mono mt-3 text-[10px] uppercase tracking-wider text-[#5B6472]">
                {r.author_name} / {t(BOOKED_VIA_KEYS[r.booked_via] ?? "booked_other")}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6">
        <ReviewForm clubSlug={clubSlug} onPosted={(r) => setReviews((prev) => [r, ...prev])} />
      </div>
    </section>
  );
}
